import { Package, MapPin, Calendar, Wrench, QrCode, AlertTriangle } from 'lucide-react';
import type { Asset, AssetStatus } from '@yawi/types';
import { useDateFormatter } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';

interface AssetCardProps {
  asset: Asset;
  onClick?: () => void;
  onGenerateQR?: () => void;
  showActions?: boolean;
}

const statusColors: Record<AssetStatus, { bg: string; text: string }> = {
  ACTIVE: { bg: 'bg-green-100', text: 'text-green-700' },
  MAINTENANCE: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  RETIRED: { bg: 'bg-slate-100', text: 'text-slate-700' },
  LOST: { bg: 'bg-red-100', text: 'text-red-700' },
};

export const AssetCard = ({ asset, onClick, onGenerateQR, showActions = true }: AssetCardProps) => {
  const { t } = useI18n();
  const { formatDateShort } = useDateFormatter();
  const statusStyle = statusColors[asset.status];

  const isMaintenanceDue = asset.nextMaintenanceAt && new Date(asset.nextMaintenanceAt) <= new Date();

  return (
    <div
      className={`bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow ${
        onClick ? 'cursor-pointer' : ''
      }`}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-500 font-mono">{asset.code}</span>
          </div>
          <h3 className="font-semibold text-slate-800 truncate">{asset.name}</h3>
          <span className="text-xs text-slate-500">{asset.categoryName}</span>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
          {t(`inventory.status.${asset.status.toLowerCase()}`)}
        </span>
      </div>

      {/* Maintenance Warning */}
      {isMaintenanceDue && (
        <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-600" />
          <span className="text-xs text-yellow-700">{t('inventory.maintenanceDue')}</span>
        </div>
      )}

      {/* Details */}
      <div className="space-y-1 text-xs text-slate-500">
        {(asset.brand || asset.model) && (
          <p className="truncate">
            {[asset.brand, asset.model].filter(Boolean).join(' - ')}
          </p>
        )}
        {asset.location && (
          <div className="flex items-center gap-2">
            <MapPin className="w-3 h-3" />
            <span className="truncate">{asset.location}</span>
          </div>
        )}
        {asset.nextMaintenanceAt && (
          <div className="flex items-center gap-2">
            <Wrench className="w-3 h-3" />
            <span>{t('inventory.nextMaintenance')}: {formatDateShort(asset.nextMaintenanceAt)}</span>
          </div>
        )}
        {asset.purchaseDate && (
          <div className="flex items-center gap-2">
            <Calendar className="w-3 h-3" />
            <span>{t('inventory.purchaseDate')}: {formatDateShort(asset.purchaseDate)}</span>
          </div>
        )}
      </div>

      {/* Value */}
      {asset.currentValue != null && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <span className="text-sm font-medium text-slate-700">
            ${Number(asset.currentValue).toLocaleString('es-MX')}
          </span>
          <span className="text-xs text-slate-500 ml-1">{t('inventory.currentValue')}</span>
        </div>
      )}

      {/* Actions */}
      {showActions && onGenerateQR && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onGenerateQR();
            }}
            className="flex items-center gap-2 text-xs text-primary-600 hover:text-primary-700"
          >
            <QrCode className="w-4 h-4" />
            {t('inventory.generateQR')}
          </button>
        </div>
      )}
    </div>
  );
};
